import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React, { Fragment } from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { TourAvailability, TourAvailabilityHotelYear, TourAvailabilityMonths } from '@model/tours';
import { Grid } from '@mui/material';
import { TourPricingMonth } from './TourPricingMonth';
import { mq } from '@styles/breakpoints';
import { monthsWeight } from '@model/common';
import { DropdownOption } from '@components/common/dropdown';
import { TripTypes } from '@model/contentful';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourPricingProps {
  theme?: Theme;
  availabilityHotelYears: Array<TourAvailabilityHotelYear>;
  year: string;
  onSubmit: (payload: TourAvailability) => void;
  testId?: string;
  tripType?: TripTypes;
  tourName: string;
  privateTourDateOptions: Array<DropdownOption>;
  isDisabled?: boolean;
  initialMonth: string;
  initialYear: string;
  totalGuests?: number;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

const ColumnsHeadingRow = styled(Grid)(({ theme }: ThemeProps) => ({
  margin: `${theme.custom.spacing.large}px 0`
}));

const ColumnHeadingLabel = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.group4.light,
  fontWeight: 'bold',
  padding: `0 ${theme.custom.spacing.large}px`,
  whiteSpace: 'nowrap'
}));

const PricingContainer = styled.div({
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
  overflow: 'hidden'
});

const AvailableDatesColumn = styled(Grid)({
  display: 'none',

  [mq.medium]: {
    display: 'block'
  }
});

/* ************************** *
 *    TourPricingComponent    *
 * ************************** */

export const TourPricingComponent = ({
  testId,
  availabilityHotelYears,
  year,
  onSubmit,
  tripType,
  tourName,
  privateTourDateOptions,
  isDisabled,
  initialMonth,
  initialYear,
  totalGuests
}: TourPricingProps) => {
  const t: any = useI18NextContext();

  const months: Array<string> = Object.entries(monthsWeight).map(([month, _]: [string, any]) =>
    month.replace(/^(\w)/, (_, x) => x.toUpperCase())
  );

  /* *** RENDERERS *** */
  const renderMonths = (availabilityMonths: TourAvailabilityMonths) =>
    months.map(
      (month: string) =>
        availabilityMonths[month] && (
          <TourPricingMonth
            key={month}
            year={year}
            month={month.toLowerCase()}
            availabilityMonth={availabilityMonths[month]}
            onSubmit={onSubmit}
            tripType={tripType}
            tourName={tourName}
            privateTourDateOptions={privateTourDateOptions}
            isDisabled={isDisabled}
            isDefault={month === initialMonth && year === initialYear}
            totalGuests={totalGuests}
          />
        )
    );

  const renderHotelAvailability = () =>
    availabilityHotelYears.map(({ hotel, months }: TourAvailabilityHotelYear, index: number) => {
      return (
        <Fragment key={hotel?.name || index}>
          <ColumnsHeadingRow container spacing={0}>
            <Grid item xs={6} md={3}>
              <ColumnHeadingLabel>{t('tour-product__pricing-column--dates')}</ColumnHeadingLabel>
            </Grid>
            <Grid item xs={5} md={5}>
              <ColumnHeadingLabel>{t('tour-product__pricing-column--price')}</ColumnHeadingLabel>
            </Grid>
            <AvailableDatesColumn item xs={1} md={4}>
              <ColumnHeadingLabel>{t('tour-product__pricing-column--dates-available')}</ColumnHeadingLabel>
            </AvailableDatesColumn>
          </ColumnsHeadingRow>
          <PricingContainer>{renderMonths(months)}</PricingContainer>
        </Fragment>
      );
    });

  return <Container data-testid={testId || TestId.tourPricing.main}>{renderHotelAvailability()}</Container>;
};

export const TourPricing = withTheme(TourPricingComponent);
